<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import { SCROLLABLE_THRESHOLD } from '@@/bits/auth_helper'
import { renderManagedTurnstile, removeTurnstile, resetTurnstile } from '@@/bits/cloudflare_turnstile'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { goBack, transformCurrentUrl, transformUrl } from '@@/bits/location'
import { warmPostHog } from '@@/bits/posthog_analytics'
import OzBackButton from '@@/library/v4/components/OzBackButton.vue'
import OzCheckBox from '@@/library/v4/components/OzCheckBox.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzInput from '@@/library/v4/components/OzInput.vue'
import OzLabelInput from '@@/library/v4/components/OzLabelInput.vue'
import OzLoadingText from '@@/library/v4/components/OzLoadingText.vue'
import OzPadletLogo from '@@/library/v4/components/OzPadletLogo.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { useActionMenuAnchor } from '@@/vuecomposables/use_action_menu_anchor'
import OzTextLink from '@@/library/v4/components/OzTextLink.vue'
import { useAuthSignupStore } from '@@/pinia/auth_signup_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import AuthActionMenu from '@@/vuecomponents/AuthActionMenu.vue'
import AuthBottomNav from '@@/vuecomponents/AuthBottomNav.vue'
import AuthLoginThirdPartyGroupButtons, {
  AuthLoginThirdPartyGroupButtonsType,
} from '@@/vuecomponents/AuthLoginThirdPartyGroupButtons.vue'
import GlobalAlertDialog from '@@/vuecomponents/GlobalAlertDialog.vue'
import GlobalConfirmationDialog from '@@/vuecomponents/GlobalConfirmationDialog.vue'
import GlobalSnackbar from '@@/vuecomponents/GlobalSnackbar.vue'
import SkipToContentLink from '@@/vuecomponents/SkipToContentLink.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const authSignupStore = useAuthSignupStore()
const windowSizeStore = useWindowSizeStore()

const SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY = 'signup_flow_email_verification_experiment'

enum AuthSignupScreen {
  EmailValidation,
  EmailVerificationWithOtp,
  PasswordSubmission,
}

enum AuthSignupUserAction {
  SubmitEmail = 'submit_email',
  SubmitPassword = 'submit_password',
  SubmitVerificationCode = 'submit_verification_code',
  ResendVerificationCode = 'resend_verification_code',
  ClickOAuthSignup = 'click_oauth_signup',
}

enum EmailVerificationExperiment {
  Enabled = 'email_verification_enabled',
  Disabled = 'email_verification_not_enabled',
}

const emailInput = ref<InstanceType<typeof OzInput>>()
const verificationCodeInput = ref<InstanceType<typeof OzInput>>()
const passwordInput = ref<InstanceType<typeof OzInput>>()
const turnstileContainer = ref<HTMLElement>()
const turnstileWidgetId = ref<string | undefined>()
const { actionMenuAnchor, openActionMenu, closeActionMenu } = useActionMenuAnchor()
const isEmailInputFocused = ref(false)
const isPasswordInputFocused = ref(false)
const isVerificationCodeInputFocused = ref(false)
const isPasswordShown = ref(false)
const initialScrollY = ref(0)

// Header elements are hidden when the input is focused on android phones.
const xHeader = computed(() => {
  return !(
    device.mobile &&
    device.android &&
    windowSizeStore.isSmallerThanTabletPortrait &&
    (isEmailInputFocused.value || isPasswordInputFocused.value)
  )
})

const isVerifyButtonDisabled = computed(() => {
  return !authSignupStore.verificationCode.length || !!authSignupStore.verificationCodeValidationMessage.length
})

const isContinueButtonDisabled = computed(() => {
  if (!authSignupStore.email.length || !!authSignupStore.emailValidationMessage.length) return true
  if (xTurnstileWidget.value && authSignupStore.humanVerificationToken == null) return true
  return false
})

const isCreateAccountButtonDisabled = computed(() => {
  return !authSignupStore.password.length || !!authSignupStore.passwordValidationMessage.length
})

const loginPageUrl = computed(() => {
  const baseLoginUrl = transformCurrentUrl({}, { path: '/auth/login' })
  if (authSignupStore.isPkceEnabled) {
    const loginUrlWithPkceParams = transformUrl(baseLoginUrl, {
      searchParams: {
        code_challenge: authSignupStore.codeChallenge as string,
        code_challenge_method: authSignupStore.codeChallengeMethod as string,
        redirect_uri: encodeURIComponent(authSignupStore.originAppRedirectUri as string),
        client_id: authSignupStore.originAppClientId as string,
        pkce_state: authSignupStore.pkceState as string,
      },
    })
    return loginUrlWithPkceParams
  } else {
    return baseLoginUrl
  }
})

const xEmailVerificationScreen = computed(() => {
  return !authSignupStore.isEmailVerified
})

const isEmailVerificationEnabled = computed(() => {
  return authSignupStore.enforceEmailVerification
})

const currentScreen = computed(() => {
  if (!isEmailVerificationEnabled.value) {
    return undefined
  }
  if (!authSignupStore.isEmailValidatedForSignup) {
    return AuthSignupScreen.EmailValidation
  }
  if (authSignupStore.isEmailValidatedForSignup && !authSignupStore.isEmailVerifiedWithOtp) {
    return AuthSignupScreen.EmailVerificationWithOtp
  }
  if (authSignupStore.isEmailVerified && authSignupStore.isEmailVerifiedWithOtp) {
    return AuthSignupScreen.PasswordSubmission
  }
  return AuthSignupScreen.EmailValidation
})

const termOfServiceAndPrivacyPolicyText = computed(() => {
  const classes = 'oz-text-link-light-secondary dark:oz-text-link-dark-secondary'

  return __(
    'By signing up, you agree to our <a href="https://legal.padlet.com/terms" target="_blank" class="%{linkClasses}">Terms of Service</a> and <a href="https://legal.padlet.com/privacy" target="_blank" class="%{linkClasses}">Privacy Policy</a>. You also admit that you are beautiful.',
    { linkClasses: classes },
  )
})

const resendCodeText = computed(() => {
  return __(
    'Didn’t get the email? <button style="vertical-align: baseline" class="font-sans bg-transparent border-none select-none oz-text-link-light-secondary dark:oz-text-link-dark-secondary font-semibold">Resend code</button>',
  )
})

watch(
  () => windowSizeStore.visualViewportHeight,
  (newHeight, oldHeight) => {
    if (device.desktop || newHeight >= oldHeight) return

    initialScrollY.value = window.scrollY

    // When people focus on the input, the keyboard will pop up
    // so the visual viewport height will be narrowed down.
    // we want to hide the keyboard when people try to scroll the page
    if (isEmailInputFocused.value) {
      document.addEventListener('scroll', blurEmailInputOnScroll)
    } else if (isPasswordInputFocused.value) {
      document.addEventListener('scroll', blurPasswordInputOnScroll)
    }
  },
)

watch(
  xEmailVerificationScreen,
  () => {
    // Skip auto-focus on mobile to avoid popping up the soft keyboard
    if (device.mobile) return

    // Only focus on current screen input field after a 1ms `setTimeout`,
    // because we need to wait for the input field to be shown in the DOM,
    // before we can focus on it.
    setTimeout(() => {
      nextTick(() => {
        if (xEmailVerificationScreen.value) {
          emailInput.value?.focusInput()
        } else {
          passwordInput.value?.focusInput()
        }
      })
    }, 1)
  },
  { immediate: true },
)

watch(
  () => authSignupStore.isAwaitingServerResponse,
  (value) => {
    const html = document.getElementsByTagName('html')[0]

    if (xEmailVerificationScreen.value) return

    // Animate the background color while waiting for the response
    // Note: Only do this when submitting the signup form
    if (value) {
      html.classList.add('dot-grid-loading')
    } else {
      html.classList.remove('dot-grid-loading')
    }
  },
)

watch(
  currentScreen,
  (value) => {
    // Skip auto-focus on mobile to avoid popping up the soft keyboard
    if (device.mobile) return

    setTimeout(() => {
      nextTick(() => {
        if (value === AuthSignupScreen.EmailValidation) {
          emailInput.value?.focusInput()
        } else if (value === AuthSignupScreen.EmailVerificationWithOtp) {
          verificationCodeInput.value?.focusInput()
        } else if (value === AuthSignupScreen.PasswordSubmission) {
          passwordInput.value?.focusInput()
        }
      })
    }, 1)
  },
  { immediate: true },
)

function onInputEmailInput(value: string): void {
  authSignupStore.setEmail(value)

  // Reset the email validation message to enable the Continue button when typing on mobile and tablet
  if (authSignupStore.emailValidationMessage.length) authSignupStore.setEmailValidationMessage('')
}

function onInputVerificationCodeInput(value: string): void {
  authSignupStore.setVerificationCode(value)

  // Reset the verification code validation message to enable the Continue button when typing on mobile and tablet
  if (authSignupStore.verificationCodeValidationMessage.length) authSignupStore.setVerificationCodeValidationMessage('')
}

function onInputPasswordInput(value: string): void {
  authSignupStore.setPassword(value)

  // Reset the password validation message to enable the Login button when typing on mobile and tablet
  if (authSignupStore.passwordValidationMessage.length) authSignupStore.setPasswordValidationMessage('')
}

function onFocusInEmailInput(): void {
  isEmailInputFocused.value = true
}

function onFocusInVerificationCodeInput(): void {
  isVerificationCodeInputFocused.value = true
}

function onFocusInPasswordInput(): void {
  isPasswordInputFocused.value = true
}

function onBlurEmailInput(): void {
  isEmailInputFocused.value = false
  document.removeEventListener('scroll', blurEmailInputOnScroll)
}

function onBlurVerificationCodeInput(): void {
  isVerificationCodeInputFocused.value = false
  document.removeEventListener('scroll', blurVerificationCodeInputOnScroll)
}

function onBlurPasswordInput(): void {
  isPasswordInputFocused.value = false
  document.removeEventListener('scroll', blurPasswordInputOnScroll)
}

function blurEmailInputOnScroll(): void {
  // Note: the scroll event is fired when people press a key on the soft keyboard only on iPad
  // we just blur the input only if the user scrolls past a certain threshold
  if (isExceededScrollableThreshold(window.scrollY)) {
    emailInput.value?.blurInput()
  }
}

function blurVerificationCodeInputOnScroll(): void {
  // Note: the scroll event is fired when people press a key on the soft keyboard only on iPad
  // we just blur the input only if the user scrolls past a certain threshold
  if (isExceededScrollableThreshold(window.scrollY)) {
    verificationCodeInput.value?.blurInput()
  }
}

function blurPasswordInputOnScroll(): void {
  // Note: the scroll event is fired when people press a key on the soft keyboard only on iPad
  // we just blur the input only if the user scrolls past a certain threshold
  if (isExceededScrollableThreshold(window.scrollY)) {
    passwordInput.value?.blurInput()
  }
}

function isExceededScrollableThreshold(currentWindowY: number): boolean {
  return (
    currentWindowY > initialScrollY.value + SCROLLABLE_THRESHOLD ||
    currentWindowY < initialScrollY.value - SCROLLABLE_THRESHOLD
  )
}

function goBackEmailVerificationScreen(event: PopStateEvent | Event): void {
  if (event.type === 'click') {
    goBack()
  }

  isPasswordShown.value = false
  authSignupStore.setIsEmailVerified(false)
  authSignupStore.setPassword('')
  authSignupStore.setPasswordValidationMessage('')
}

function goBackToPreviousScreen(): void {
  if (currentScreen.value === AuthSignupScreen.PasswordSubmission) {
    isPasswordShown.value = false
    authSignupStore.setIsEmailVerifiedWithOtp(false)
    authSignupStore.setPassword('')
    authSignupStore.setPasswordValidationMessage('')
  } else if (currentScreen.value === AuthSignupScreen.EmailVerificationWithOtp) {
    authSignupStore.setVerificationCode('')
    authSignupStore.setVerificationCodeValidationMessage('')
    authSignupStore.setIsEmailValidatedForSignup(false)
  }
}

async function handleSubmitSignupForm(): Promise<void> {
  if (isEmailVerificationEnabled.value) {
    await submitEmailOtpVerificationEnabled()
  } else {
    await submit()
  }
}

async function submitEmailOtpVerificationEnabled() {
  if (currentScreen.value === AuthSignupScreen.EmailValidation) {
    // Blur the email input to prevent modifying the input value when submitting the form by clicking "Enter" key
    emailInput.value?.blurInput()

    trackEvent(
      SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY,
      AuthSignupUserAction.SubmitEmail,
      EmailVerificationExperiment.Enabled,
    )
    await authSignupStore.continueSignupWithEmail()

    // Reset Turnstile so a fresh token is obtained for the next attempt.
    // The backend consumes the token during check_email, so re-sending
    // the same token on retry would fail with "timeout-or-duplicate".
    if (turnstileWidgetId.value != null) {
      resetTurnstile(turnstileWidgetId.value)
    }

    // Re-focus the email input if there is an error message
    if (authSignupStore.emailValidationMessage.length) {
      emailInput.value?.focusInput()
    }

    if (authSignupStore.isEmailValidatedForSignup) {
      // Enable the browser back button on signup page
      window.history.pushState({}, '', window.location.href)
    }

    window.addEventListener('popstate', goBackToPreviousScreen)
  } else if (currentScreen.value === AuthSignupScreen.EmailVerificationWithOtp) {
    if (authSignupStore.isAwaitingServerResponse || isVerifyButtonDisabled.value) {
      return
    }

    // Blur the verification code input to prevent modifying the input value when submitting the form by clicking "Enter" key
    verificationCodeInput.value?.blurInput()

    trackEvent(
      SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY,
      AuthSignupUserAction.SubmitVerificationCode,
      EmailVerificationExperiment.Enabled,
    )
    await authSignupStore.continueWithOtpVerification()

    // Re-focus the verification code input if there is an error message
    if (authSignupStore.verificationCodeValidationMessage.length) {
      verificationCodeInput.value?.focusInput()
    }

    if (authSignupStore.isEmailVerifiedWithOtp) {
      // Enable the browser back button on signup page
      window.history.pushState({}, '', window.location.href)
    }
    window.addEventListener('popstate', goBackToPreviousScreen)
  } else {
    // Blur the password input to prevent modifying the input value when submitting the form by clicking "Enter" key
    passwordInput.value?.blurInput()

    trackEvent(
      SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY,
      AuthSignupUserAction.SubmitPassword,
      EmailVerificationExperiment.Enabled,
    )
    await authSignupStore.completeSignupWithPassword()

    // Re-focus the password input if there is an error message
    if (authSignupStore.passwordValidationMessage.length) {
      passwordInput.value?.focusInput()
    }
  }
}

async function submit(): Promise<void> {
  if (xEmailVerificationScreen.value) {
    // Blur the email input to prevent modifying the input value when submitting the form by clicking "Enter" key
    emailInput.value?.blurInput()

    trackEvent(
      SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY,
      AuthSignupUserAction.SubmitEmail,
      EmailVerificationExperiment.Disabled,
    )
    await authSignupStore.continueSignupWithEmail()

    // Reset Turnstile so a fresh token is obtained for the next attempt.
    // The backend consumes the token during check_email, so re-sending
    // the same token on retry would fail with "timeout-or-duplicate".
    if (turnstileWidgetId.value != null) {
      resetTurnstile(turnstileWidgetId.value)
    }

    // Re-focus the email input if there is an error message
    if (authSignupStore.emailValidationMessage.length) {
      emailInput.value?.focusInput()
    }

    if (authSignupStore.isEmailVerified && !window.history.state?.didPushState) {
      // Enable the browser back button on signup page
      // didPushState: true to prevent pushing redundant states
      window.history.pushState({ didPushState: true }, '', window.location.href)
    }

    window.addEventListener('popstate', goBackEmailVerificationScreen)
  } else {
    // Blur the password input to prevent modifying the input value when submitting the form by clicking "Enter" key
    passwordInput.value?.blurInput()

    trackEvent(
      SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY,
      AuthSignupUserAction.SubmitPassword,
      EmailVerificationExperiment.Disabled,
    )
    await authSignupStore.completeSignupWithPassword()

    // Re-focus the password input if there is an error message
    if (authSignupStore.passwordValidationMessage.length) {
      passwordInput.value?.focusInput()
    }
  }
}

async function resendCode(): Promise<void> {
  if (!authSignupStore.isAwaitingServerResponse) {
    trackEvent(
      SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY,
      AuthSignupUserAction.ResendVerificationCode,
      EmailVerificationExperiment.Enabled,
    )
    await authSignupStore.resendVerificationCode()
  }
}

function trackOAuthSignupClick(methodName: string): void {
  trackEvent(SIGNUP_VERIFICATION_EXPERIMENT_EVENT_TRACKING_CATEGORY, AuthSignupUserAction.ClickOAuthSignup, methodName)
}

// --- Cloudflare Turnstile ---

const xTurnstileWidget = computed(() => {
  return isAppUsing('turnstileOnSignup')
})

async function initTurnstile(): Promise<void> {
  if (!xTurnstileWidget.value || turnstileContainer.value == null) return

  const siteKey = window.ww?.vueStartingState?.turnstileSiteKey
  if (siteKey == null) return

  try {
    const widgetId = await renderManagedTurnstile({
      siteKey,
      container: turnstileContainer.value,
      action: 'signup',
      theme: 'auto',
      onVerify: (token: string) => {
        authSignupStore.humanVerificationToken = token
      },
      onError: () => {
        // Fail closed — block signup when Turnstile encounters an error
        authSignupStore.humanVerificationToken = null
      },
      onExpire: () => {
        authSignupStore.humanVerificationToken = null
      },
      onTimeout: () => {
        authSignupStore.humanVerificationToken = null
      },
    })
    turnstileWidgetId.value = widgetId
  } catch {
    // Fail closed — if Turnstile script fails to load, block signup.
    // humanVerificationToken stays null, which keeps the continue button disabled.
    authSignupStore.humanVerificationToken = null
  }
}

function cleanupTurnstile(): void {
  if (turnstileWidgetId.value != null) {
    try {
      removeTurnstile(turnstileWidgetId.value)
    } catch {
      // Widget's DOM container may already be destroyed by v-if — safe to ignore
    }
    turnstileWidgetId.value = undefined
  }
}

// Re-render the Turnstile widget whenever its container DOM element is (re-)created.
// The container uses `v-if`, so navigating away destroys it and navigating back
// creates a fresh element. This watcher covers both the initial mount and any
// subsequent re-creation.
watch(turnstileContainer, (el) => {
  if (el != null) {
    cleanupTurnstile()
    void initTurnstile()
  }
})

onMounted(() => {
  // Warm the PostHog client so the "Signed up" conversion capture in completeSignupWithPassword
  // is ready before the post-signup redirect unloads the page.
  warmPostHog('auth_signup_app')
})

onBeforeUnmount(() => {
  cleanupTurnstile()
  window.removeEventListener('popstate', goBackEmailVerificationScreen)
  window.removeEventListener('popstate', goBackToPreviousScreen)
})
</script>

<template>
  <div class="relative flex flex-col items-center">
    <header
      v-show="xHeader"
      :class="[
        'fixed flex items-center justify-between box-border w-full transition-all',
        device.desktop || xEmailVerificationScreen ? 'h-14 ps-5 pe-4' : 'h-18 px-4 tablet-portrait:ps-6',
      ]"
    >
      <SkipToContentLink />

      <!-- UI for the email verification screen -->
      <OzPadletLogo v-if="device.desktop || xEmailVerificationScreen" :class="[{ invisible: device.electronApp }]" />
      <OzBackButton
        v-else-if="!isEmailVerificationEnabled && !xEmailVerificationScreen"
        :disabled="authSignupStore.isAwaitingServerResponse"
        @click="goBackEmailVerificationScreen"
      />
      <OzBackButton
        v-else-if="isEmailVerificationEnabled && currentScreen !== AuthSignupScreen.EmailValidation"
        :disabled="authSignupStore.isAwaitingServerResponse"
        @click="goBackToPreviousScreen"
      />

      <div
        v-if="device.desktop && !windowSizeStore.isSmallerThanTabletPortrait"
        class="flex items-center text-body-small"
      >
        <span class="text-dark-text-200 dark:text-light-text-200">
          {{ __('Already a member?') }}
        </span>

        <OzTextLink
          :href="loginPageUrl"
          data-testid="loginUrlButton"
          :text="__('Log in')"
          class="font-semibold ms-1 me-2"
        />
      </div>
      <OzPlainButton
        v-else-if="xEmailVerificationScreen"
        :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
        :size-preset="OzPlainButtonSizePreset.H28px"
        :aria-label="__('Open more menu dialog')"
        @click="openActionMenu"
      >
        <OzIcon :size="20" name="more_vertical" />
      </OzPlainButton>
    </header>

    <main
      id="main-content"
      :class="[
        'fixed',
        'bottom-0',
        'flex',
        'flex-col',
        'box-border',
        'w-full',
        'py-2 px-4',
        'overflow-y-auto',
        device.desktop || xEmailVerificationScreen ? 'max-h-[calc(100vh-3.5rem)]' : 'max-h-[calc(100vh-4.5rem)]',
        'tablet-portrait:relative tablet-portrait:w-[375px] tablet-portrait:p-0 tablet-portrait:px-4 tablet-portrait:mt-32 tablet-portrait:max-h-none tablet-portrait:overflow-y-visible',
      ]"
      tabindex="-1"
    >
      <OzBackButton
        v-if="device.desktop && !isEmailVerificationEnabled && !xEmailVerificationScreen"
        class="mb-5"
        :disabled="authSignupStore.isAwaitingServerResponse"
        @click="goBackEmailVerificationScreen"
      />
      <OzBackButton
        v-else-if="device.desktop && isEmailVerificationEnabled && currentScreen !== AuthSignupScreen.EmailValidation"
        class="mb-5"
        :disabled="authSignupStore.isAwaitingServerResponse"
        @click="goBackToPreviousScreen"
      />
      <div
        v-if="xEmailVerificationScreen || currentScreen === AuthSignupScreen.EmailValidation"
        class="text-dark-text-100 dark:text-light-text-100 mb-3 tablet-portrait:mb-5"
      >
        <h1 class="w-56 text-login-heading-1 font-semibold px-2 mb-1">
          {{ __('Sign up') }}
        </h1>
        <h2 class="text-login-body px-2">
          {{ __('You belong here') }}
        </h2>
      </div>

      <div
        v-else-if="currentScreen === AuthSignupScreen.EmailVerificationWithOtp"
        class="text-dark-text-100 dark:text-light-text-100 mb-3 tablet-portrait:mb-5"
      >
        <h1 class="text-login-heading-1 font-semibold px-2 mb-1">
          {{ __('Verify your email') }}
        </h1>
        <h2 class="text-dark-text-200 dark:text-light-text-200 text-body-posts px-2">
          {{ authSignupStore.email }}
        </h2>

        <AuthLoginThirdPartyGroupButtons
          :login-methods="authSignupStore.thirdPartyAuthVerifyMethods"
          :type="AuthLoginThirdPartyGroupButtonsType.Continue"
          class="my-5"
        />
        <h2 v-if="Object.keys(authSignupStore.thirdPartyAuthVerifyMethods).length !== 0" class="text-body-posts px-2">
          {{ __('Or, enter the verification code we have sent to your email. You only need to do this once.') }}
        </h2>
        <h2 v-else class="text-body-posts px-2">
          {{
            __(
              'We sent you a temporary verification code. Enter it to verify your email. You only need to do this once.',
            )
          }}
        </h2>
      </div>

      <div v-else class="text-dark-text-100 dark:text-light-text-100 mb-3 tablet-portrait:mb-5">
        <h1 class="w-56 text-login-heading-1 font-semibold px-2 mb-1">
          {{ __('Sign up with email') }}
        </h1>
        <h2 class="text-login-body px-2">
          {{ authSignupStore.email }}
        </h2>
      </div>

      <AuthLoginThirdPartyGroupButtons
        v-if="
          ((xEmailVerificationScreen && !isEmailVerificationEnabled) ||
            currentScreen === AuthSignupScreen.EmailValidation) &&
          !!Object.keys(authSignupStore.thirdPartyAuthLoginMethods).length
        "
        :login-methods="authSignupStore.thirdPartyAuthLoginMethods"
        :type="AuthLoginThirdPartyGroupButtonsType.SignUp"
        class="mb-3 tablet-portrait:mb-5"
        @select="trackOAuthSignupClick"
      />

      <form
        novalidate
        :class="['flex flex-col mb-3', { 'tablet-portrait:mb-[72px]': !windowSizeStore.isSmallerThanTabletPortrait }]"
        @submit.stop.prevent="handleSubmitSignupForm"
        @keydown.enter.stop.prevent="handleSubmitSignupForm"
      >
        <!--
          Use `model-value` and `@input` here instead of `v-model`
          because the input value doesn't get updated on every keypress on android when using `v-model`
          @see https://github.com/vuejs/vue/issues/9777#issuecomment-478831263
        -->

        <template v-if="isEmailVerificationEnabled">
          <!-- Display the email input on the email validation screen -->
          <OzLabelInput v-show="currentScreen === AuthSignupScreen.EmailValidation" class="mb-4">
            <template #label>
              <label
                for="email-input"
                :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
              >
                {{ __('Or, sign up with email') }}
              </label>
            </template>

            <template #input
              ><OzInput
                id="email-input"
                ref="emailInput"
                name="email"
                type="email"
                autocomplete="email"
                test-id="emailInput"
                :disabled="authSignupStore.isAwaitingServerResponse"
                :model-value="authSignupStore.email"
                :validation-message="authSignupStore.emailValidationMessage"
                placeholder="harrypotter@hogwarts.edu"
                :valid="authSignupStore.isEmailValid"
                class="bg-light-ui-100 dark:bg-dark-ui-100"
                :required="true"
                @update:modelValue="onInputEmailInput"
                @focusin="onFocusInEmailInput"
                @blur="onBlurEmailInput"
            /></template>
          </OzLabelInput>

          <!-- Cloudflare Turnstile managed widget -->
          <div
            v-if="xTurnstileWidget && currentScreen === AuthSignupScreen.EmailValidation"
            ref="turnstileContainer"
            class="mb-4 w-full"
            data-testid="turnstileWidget"
          />

          <!-- Display the verification code input on the email verification with OTP screen -->
          <OzLabelInput v-show="currentScreen === AuthSignupScreen.EmailVerificationWithOtp" class="mb-4">
            <template #label>
              <label
                for="verification-code-input"
                :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
              >
                {{ __('Verification code') }}
              </label>
            </template>

            <template #input
              ><OzInput
                id="verification-code-input"
                ref="verificationCodeInput"
                name="verificationCode"
                type="text"
                test-id="verificationCodeInput"
                :model-value="authSignupStore.verificationCode"
                :disabled="authSignupStore.isAwaitingServerResponse"
                :validation-message="authSignupStore.verificationCodeValidationMessage"
                :valid="authSignupStore.isVerificationCodeValid"
                inputmode="numeric"
                pattern="[0-9]*"
                :placeholder="__('Paste code from your email')"
                autocomplete="off"
                class="bg-light-ui-100 dark:bg-dark-ui-100"
                :required="true"
                @update:modelValue="onInputVerificationCodeInput"
                @focusin="onFocusInVerificationCodeInput"
                @blur="onBlurVerificationCodeInput"
            /></template>
          </OzLabelInput>

          <template v-if="currentScreen === AuthSignupScreen.PasswordSubmission">
            <AuthLoginThirdPartyGroupButtons
              v-if="isAppUsing('schoolLibrarySSO')"
              :login-methods="authSignupStore.userThirdPartyAuthLoginMethods"
              :type="AuthLoginThirdPartyGroupButtonsType.Continue"
              class="mb-5"
            />
            <!-- Display the password input on the signup with password screen -->
            <OzLabelInput
              v-show="authSignupStore.isPasswordSignupEnabled"
              :input-descriptor-id="'password-input-descriptor'"
              :input-descriptor="
                __('Minimum of %{minimumLength} characters', { minimumLength: authSignupStore.minimumPasswordLength })
              "
              class="mb-4"
            >
              <template #label>
                <label
                  for="password-input"
                  :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
                >
                  {{ __('Choose a password') }}
                </label>
              </template>

              <template #inputAccessory>
                <OzCheckBox
                  :value="isPasswordShown"
                  :label="__('Show')"
                  :aria-label="
                    __('Show password as plain text. Warning: this will display your password on the screen.')
                  "
                  :dir="dir()"
                  class="px-2"
                  @input="isPasswordShown = !isPasswordShown"
                />
              </template>

              <template #input
                ><OzInput
                  id="password-input"
                  ref="passwordInput"
                  name="password"
                  autocomplete="new-password"
                  autocapitalize="none"
                  aria-describedby="password-input-descriptor"
                  :disabled="authSignupStore.isAwaitingServerResponse"
                  :type="isPasswordShown ? 'text' : 'password'"
                  test-id="passwordInput"
                  :model-value="authSignupStore.password"
                  :validation-message="authSignupStore.passwordValidationMessage"
                  :placeholder="__('Choose wisely')"
                  :valid="authSignupStore.isPasswordValid"
                  class="bg-light-ui-100 dark:bg-dark-ui-100"
                  :required="true"
                  @update:modelValue="onInputPasswordInput"
                  @focusin="onFocusInPasswordInput"
                  @blur="onBlurPasswordInput"
              /></template>
            </OzLabelInput>
          </template>

          <div class="flex justify-center tablet-portrait:justify-end">
            <!-- Display the Continue button on the email validation screen -->
            <template v-if="currentScreen === AuthSignupScreen.EmailValidation">
              <OzLoadingText
                v-if="authSignupStore.isAwaitingServerResponse"
                :text="__('Checking email...')"
                class="text-heading-4 text-center font-semibold px-5 py-2"
              />
              <OzContainedButton
                v-else
                data-testid="continueButton"
                :color-scheme="OzContainedButtonColorScheme.Primary"
                :disabled="isContinueButtonDisabled"
                class="w-full tablet-portrait:w-auto"
                >{{ __('Continue') }}</OzContainedButton
              >
            </template>

            <!-- Display the Verify button on the email verification with OTP screen -->
            <OzContainedButton
              v-else-if="currentScreen === AuthSignupScreen.EmailVerificationWithOtp"
              data-testid="verifyEmailButton"
              :color-scheme="OzContainedButtonColorScheme.Primary"
              :disabled="authSignupStore.isAwaitingServerResponse || isVerifyButtonDisabled"
              class="w-full tablet-portrait:w-auto"
              >{{ __('Verify') }}</OzContainedButton
            >

            <!-- Display the Create account button on the signup with password screen -->
            <template v-else>
              <OzLoadingText
                v-if="authSignupStore.isAwaitingServerResponse"
                :text="__('Creating account...')"
                class="text-heading-4 text-center font-semibold px-5 py-2"
              />
              <OzContainedButton
                v-else-if="authSignupStore.isPasswordSignupEnabled"
                :disabled="isCreateAccountButtonDisabled"
                :color-scheme="OzContainedButtonColorScheme.Primary"
                data-testid="createButton"
                class="w-full tablet-portrait:w-auto"
              >
                {{ __('Create account') }}
              </OzContainedButton>
            </template>
          </div>
        </template>

        <template v-else>
          <!-- Display the email input on the email verification screen -->
          <OzLabelInput v-show="xEmailVerificationScreen" class="mb-4">
            <template #label>
              <label
                for="email-input"
                :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
              >
                {{ __('Or, sign up with email') }}
              </label>
            </template>

            <template #input
              ><OzInput
                id="email-input"
                ref="emailInput"
                name="email"
                type="email"
                autocomplete="email"
                test-id="emailInput"
                :disabled="authSignupStore.isAwaitingServerResponse"
                :model-value="authSignupStore.email"
                :validation-message="authSignupStore.emailValidationMessage"
                placeholder="harrypotter@hogwarts.edu"
                :valid="authSignupStore.isEmailValid"
                class="bg-light-ui-100 dark:bg-dark-ui-100"
                :required="true"
                @update:modelValue="onInputEmailInput"
                @focusin="onFocusInEmailInput"
                @blur="onBlurEmailInput"
            /></template>
          </OzLabelInput>

          <!-- Cloudflare Turnstile managed widget (non-verification path) -->
          <div
            v-if="xTurnstileWidget && xEmailVerificationScreen"
            ref="turnstileContainer"
            class="mb-4 w-full"
            data-testid="turnstileWidget"
          />

          <template v-if="!xEmailVerificationScreen">
            <AuthLoginThirdPartyGroupButtons
              v-if="isAppUsing('schoolLibrarySSO')"
              :login-methods="authSignupStore.userThirdPartyAuthLoginMethods"
              :type="AuthLoginThirdPartyGroupButtonsType.Continue"
              class="mb-5"
            />
            <!-- Display the password input on the signup with password screen -->
            <OzLabelInput
              v-show="authSignupStore.isPasswordSignupEnabled"
              :input-descriptor-id="'password-input-descriptor'"
              :input-descriptor="
                __('Minimum of %{minimumLength} characters', { minimumLength: authSignupStore.minimumPasswordLength })
              "
              class="mb-4"
            >
              <template #label>
                <label
                  for="password-input"
                  :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
                >
                  {{ __('Choose a password') }}
                </label>
              </template>

              <template #inputAccessory>
                <OzCheckBox
                  :value="isPasswordShown"
                  :label="__('Show')"
                  :aria-label="
                    __('Show password as plain text. Warning: this will display your password on the screen.')
                  "
                  :dir="dir()"
                  class="px-2"
                  @input="isPasswordShown = !isPasswordShown"
                />
              </template>

              <template #input
                ><OzInput
                  id="password-input"
                  ref="passwordInput"
                  name="password"
                  autocomplete="new-password"
                  aria-describedby="password-input-descriptor"
                  :disabled="authSignupStore.isAwaitingServerResponse"
                  :type="isPasswordShown ? 'text' : 'password'"
                  test-id="passwordInput"
                  :model-value="authSignupStore.password"
                  :validation-message="authSignupStore.passwordValidationMessage"
                  :placeholder="__('Choose wisely')"
                  :valid="authSignupStore.isPasswordValid"
                  class="bg-light-ui-100 dark:bg-dark-ui-100"
                  :required="true"
                  @update:modelValue="onInputPasswordInput"
                  @focusin="onFocusInPasswordInput"
                  @blur="onBlurPasswordInput"
              /></template>
            </OzLabelInput>
          </template>

          <div class="flex justify-center tablet-portrait:justify-end">
            <!-- Display the Continue button on the email verification screen -->
            <template v-if="xEmailVerificationScreen">
              <OzLoadingText
                v-if="authSignupStore.isAwaitingServerResponse"
                :text="__('Checking email...')"
                class="text-heading-4 text-center font-semibold px-5 py-2"
              />
              <OzContainedButton
                v-else
                data-testid="continueButton"
                :color-scheme="OzContainedButtonColorScheme.Primary"
                :disabled="isContinueButtonDisabled"
                class="w-full tablet-portrait:w-auto"
                >{{ __('Continue') }}</OzContainedButton
              >
            </template>

            <!-- Display the Create account button on the signup with password screen -->
            <template v-else>
              <OzLoadingText
                v-if="authSignupStore.isAwaitingServerResponse"
                :text="__('Creating account...')"
                class="text-heading-4 text-center font-semibold px-5 py-2"
              />
              <OzContainedButton
                v-else-if="authSignupStore.isPasswordSignupEnabled"
                :disabled="isCreateAccountButtonDisabled"
                :color-scheme="OzContainedButtonColorScheme.Primary"
                data-testid="createButton"
                class="w-full tablet-portrait:w-auto"
              >
                {{ __('Create account') }}
              </OzContainedButton>
            </template>
          </div>
        </template>
      </form>

      <p
        class="text-body-small-posts text-dark-text-200 dark:text-light-text-200 text-center tablet-portrait:text-start px-2"
      >
        <template v-if="xEmailVerificationScreen && windowSizeStore.isSmallerThanTabletPortrait">
          <span>
            {{ __('Already a member?') }}
          </span>

          <OzTextLink :href="loginPageUrl" :text="__('Log in')" class="font-semibold ms-0.5" />
        </template>

        <template v-else-if="isEmailVerificationEnabled && currentScreen === AuthSignupScreen.EmailVerificationWithOtp">
          <form @submit.prevent="resendCode">
            <p
              class="text-body-small-posts text-dark-text-200 dark:text-light-text-200 text-center tablet-portrait:text-start"
              v-safe-html="resendCodeText"
            />
          </form>
        </template>

        <span
          v-else-if="!windowSizeStore.isSmallerThanTabletPortrait || !xEmailVerificationScreen"
          class="text-body-extra-small"
          v-safe-html="termOfServiceAndPrivacyPolicyText"
        />
      </p>

      <AuthActionMenu v-if="actionMenuAnchor" :anchor="actionMenuAnchor" @cancel="closeActionMenu" />
      <GlobalAlertDialog />
      <GlobalConfirmationDialog />
      <GlobalSnackbar />
    </main>

    <AuthBottomNav v-if="device.desktop" class="fixed start-6 bottom-6 hidden tablet-portrait:flex" />
  </div>
</template>

<style lang="postcss">
@import '@@/styles/4/kit';
@import '@@/styles/4/dot_grid';

* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

html {
  background: var(--dot-grid);
  @apply overflow-hidden;
  background-position: start top;
  @apply h-screen;

  font-size: 16px;
}

@media screen and (orientation: landscape) {
  html {
    @apply overflow-auto;
  }
}
</style>
